import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { eur, formatApiError } from "@/lib/api";
import { CreditCard, Copy, Send } from "lucide-react";

export default function CardPayments({ bookingId, phone }) {
  const [d, setD] = useState(null);
  const [amount, setAmount] = useState("");
  const [link, setLink] = useState(null);
  const load = useCallback(() => { api.get(`/bookings/${bookingId}/card-payments`).then((r) => setD(r.data)).catch(() => {}); }, [bookingId]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const toggle = async () => {
    try { await api.patch(`/bookings/${bookingId}/card-payments`, { enabled: !d.enabled }); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const make = async (source) => {
    try {
      const r = await api.post(`/bookings/${bookingId}/card-payments/link`, { origin_url: window.location.origin, amount: amount ? Number(amount) : null, source });
      if (source === "checkout") window.open(r.data.checkout_url, "_blank");
      else setLink(r.data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const payPage = d.pay_token ? `${window.location.origin}/paga/${bookingId}?t=${d.pay_token}` : "";
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Link copiato"); };
  const wa = (t) => window.open(`https://api.whatsapp.com/send?phone=${(phone || "").replace(/[^\d]/g, "").replace(/^00/, "")}&text=${encodeURIComponent(t)}`, "_blank");
  const btn = "px-2.5 py-1.5 rounded-md text-xs font-semibold";
  return (
    <div className="sm:col-span-2 rounded-lg border border-violet-200 bg-violet-50/40 p-3" data-testid="card-payments">
      <label className="flex items-center gap-2 text-sm font-semibold text-slate-800 cursor-pointer">
        <input type="checkbox" checked={d.enabled} onChange={toggle} className="h-4 w-4 accent-violet-600" data-testid="card-payments-toggle" />
        <CreditCard size={15} className="text-violet-600" /> Pagamento con carta (Stripe) per questa prenotazione
      </label>
      {d.enabled && (
        <div className="mt-2 space-y-2 text-xs">
          <div className="text-slate-600" data-testid="card-next-due">Prossimo importo: {d.next_due ? <b>{d.next_due.label} {eur(d.next_due.amount)}</b> : <b className="text-emerald-700">tutto pagato</b>}</div>
          <div className="flex flex-wrap items-center gap-2">
            <input data-testid="card-amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Importo diverso (opz.)" className="w-40 px-2 py-1.5 rounded border border-slate-300" />
            <button type="button" data-testid="card-make-link" onClick={() => make("link")} className={`${btn} bg-violet-600 text-white`}>Crea link di pagamento</button>
            <button type="button" data-testid="card-checkout-now" onClick={() => make("checkout")} className={`${btn} border border-violet-300 text-violet-700 bg-white`}>Incassa ora con carta (check-out)</button>
          </div>
          {link && (
            <div className="flex flex-wrap items-center gap-2 bg-white rounded border border-violet-200 px-2 py-1.5" data-testid="card-link">
              <span className="font-semibold">{link.label} {eur(link.amount)}</span>
              <span className="truncate max-w-[220px] text-slate-500">{link.checkout_url}</span>
              <button type="button" onClick={() => copy(link.checkout_url)} className="text-violet-700" data-testid="card-link-copy"><Copy size={13} /></button>
              <button type="button" onClick={() => wa(`Ecco il link per pagare ${link.label.toLowerCase()} di ${eur(link.amount)} con carta: ${link.checkout_url}`)} className="text-green-700" data-testid="card-link-wa"><Send size={13} /></button>
              <span className="w-full text-[10px] text-slate-400">Il link scade dopo 24 ore. Per un link sempre valido usa quello della pagina di pagamento qui sotto.</span>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 text-slate-500" data-testid="card-pay-page">
            Pagina di pagamento (anche sul voucher): <span className="truncate max-w-[260px] text-slate-700">{payPage}</span>
            <button type="button" onClick={() => copy(payPage)} className="text-violet-700"><Copy size={13} /></button>
            <button type="button" onClick={() => wa(`Puoi pagare online con carta qui: ${payPage}`)} className="text-green-700"><Send size={13} /></button>
          </div>
          {d.paid.length > 0 && (
            <div className="text-emerald-700" data-testid="card-paid-list">Pagato con carta: {d.paid.map((p) => `${p.label || p.kind} ${eur(p.amount)} (${p.paid_at.slice(0, 10)})`).join(" · ")}</div>
          )}
        </div>
      )}
    </div>
  );
}
